/**
 * Represents an empty object and nothing else.
 * Because the type `{}` is unreliable in TypeScript and should not be used.
 *
 * @source https://github.com/sindresorhus/type-fest/blob/main/source/empty-object.d.ts
 */
export type EmptyObject = { [emptyObjectSymbol]?: never }

declare const emptyObjectSymbol: unique symbol

// Object entry helpers
type ValueOf<T> = T[keyof T]
type ObjectEntries<T> = [keyof T, ValueOf<T>][]

/**
 * Same as `Object.entries()` but with type inference
 *
 * @see {@link https://stackoverflow.com/a/75337277}
 */
export function objectEntries<T extends object>(obj: T): ObjectEntries<T> {
  return Object.entries(obj) as ObjectEntries<T>
}

/**
 * Runtime check whether a value matches an Enum (or fake Enum).
 *
 * Note: should only be used with String Enums and Objects, not Numeric Enums!
 */
export function isValueInEnum(value: unknown, enumObj: object): boolean {
  return Object.values(enumObj).includes(value)
}

/**
 * Extends `Array.includes()` with types, for use with const arrays.
 *
 * @see {@link https://stackoverflow.com/a/76415481}
 */
export function arrayIncludes<T extends U, U>(
  array: ReadonlyArray<T>,
  searchElement: U,
): searchElement is T {
  return array.includes(searchElement as T)
}

/**
 * Checks that a given value is an `Array` and is also empty.
 *
 * Unfortunately, the `isEmpty` function found in many libs
 * like lodash has unexpected results with non-object values.
 * This function is safer to use when we want to explicitly
 * work with `Array`s.
 */
export function isEmptyArray(array): array is [] {
  return Array.isArray(array) && array.length === 0
}
